import styled, { css } from "styled-components";

type SampleButtonProps = {
  startTime: number;       // segundos absolutos en el audio
  duration: number;        // duración del run completo (info en hover)
  preview: string;         // texto a mostrar como preview
  isPlaying: boolean;
  progress: number;        // 0..1, solo aplica si isPlaying
  onPlay: () => void;
  /**
   * Segundos de padding aplicados antes del chunk durante la reproducción.
   * Cuando paddingBefore + paddingAfter > 0, la barra de progreso muestra
   * los segmentos de padding en gris y el chunk real en accent — para que
   * el usuario vea cuándo empieza y termina el contenido a validar.
   *
   * Si paddingBefore y paddingAfter son 0 (o se omiten), la barra es
   * uniformemente accent — comportamiento de antes de Fase 24.
   */
  paddingBefore?: number;
  /** Segundos de padding aplicados después del chunk. Ver paddingBefore. */
  paddingAfter?: number;
  /**
   * Duración del chunk real en segundos. Si se omite se usa `duration`,
   * que en algunos callers es la duración del run y no la del chunk
   * que efectivamente suena. Para que las zonas reflejen la realidad
   * conviene pasarlo explícito.
   */
  chunkDuration?: number;
};

// Formato mm:ss a partir de segundos.
function formatMmSs(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function SampleButton({
  startTime,
  duration,
  preview,
  isPlaying,
  progress,
  onPlay,
  paddingBefore = 0,
  paddingAfter = 0,
  chunkDuration,
}: SampleButtonProps) {
  // Cálculo de los porcentajes de cada zona dentro de la duración total
  // que va a sonar (paddingBefore + chunk + paddingAfter).
  const chunk = chunkDuration ?? duration;
  const total = paddingBefore + chunk + paddingAfter;
  const beforePct = total > 0 ? (paddingBefore / total) * 100 : 0;
  const chunkPct = total > 0 ? (chunk / total) * 100 : 100;

  return (
    <Wrapper
      $playing={isPlaying}
      onClick={onPlay}
      type="button"
      title={`Run de ${duration.toFixed(0)}s — click para reproducir 5s`}
    >
      <PlayIcon $playing={isPlaying}>{isPlaying ? "■" : "▶"}</PlayIcon>

      <Body>
        <Timestamp>@ {formatMmSs(startTime)}</Timestamp>
        <Preview>"{preview}"</Preview>
      </Body>

      {isPlaying && (
        <ProgressBar
          $progress={progress}
          $beforePct={beforePct}
          $chunkPct={chunkPct}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.button<{ $playing: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  width: 100%;
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 120ms, border-color 120ms;
  overflow: hidden;

  &:hover {
    background: ${({ theme }) => theme.color.background};
    border-color: ${({ theme }) => theme.color.borderStrong};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accent};
    outline-offset: 2px;
  }

  ${({ $playing, theme }) =>
    $playing &&
    css`
      background: ${theme.color.accentMuted};
      border-color: ${theme.color.accent};
    `}
`;

const PlayIcon = styled.span<{ $playing: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ theme, $playing }) =>
    $playing ? theme.color.accent : theme.color.background};
  color: ${({ theme, $playing }) =>
    $playing ? "white" : theme.color.textPrimary};
  font-size: ${({ theme }) => theme.font.size.xs};
  transition: background 120ms, color 120ms;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.space.sm};
`;

const Timestamp = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  flex-shrink: 0;
`;

const Preview = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
`;

/*
 * Barra de progreso bicolor (Fase 24).
 *
 * El gradient tiene tres zonas con hard-stops:
 *   [textMuted ............]  paddingBefore  → gris
 *   [accent .........]        chunk real     → azul
 *   [textMuted ............]  paddingAfter   → gris
 *
 * Con `background-size: ${100 / progress}%` el gradient queda anclado al
 * ancho TOTAL de la pista (no al ancho actual de la barra). Así, mientras
 * la barra crece de 0% a 100%, los colores que se van revelando corresponden
 * exactamente a la posición temporal dentro de la reproducción.
 *
 * IMPORTANTE — el `transition` incluye TANTO `width` COMO `background-size`.
 * Si solo transicionara `width`, entonces el width se interpolaría suavemente
 * cada 50 ms pero el background-size saltaría de golpe — eso desincroniza el
 * borde gris→azul del avance del ancho y produce una vibración visible
 * cuando la barra cruza la zona del chunk. Transicionando ambos juntos, el
 * producto width × backgroundSize se mantiene constante y el borde queda
 * matemáticamente fijo en la misma posición de la pista.
 */
const ProgressBar = styled.div<{
  $progress: number;
  $beforePct: number;
  $chunkPct: number;
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: ${({ $progress }) => $progress * 100}%;
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.color.accentHedge} 0%,
    ${({ theme }) => theme.color.accentHedge} ${({ $beforePct }) => $beforePct}%,
    ${({ theme }) => theme.color.accent} ${({ $beforePct }) => $beforePct}%,
    ${({ theme }) => theme.color.accent}
      ${({ $beforePct, $chunkPct }) => $beforePct + $chunkPct}%,
    ${({ theme }) => theme.color.accentHedge}
      ${({ $beforePct, $chunkPct }) => $beforePct + $chunkPct}%,
    ${({ theme }) => theme.color.accentHedge} 100%
  );
  background-size: ${({ $progress }) =>
    $progress > 0.001 ? `${100 / $progress}% 100%` : "100% 100%"};
  transition: width 50ms linear, background-size 50ms linear;
`;
